/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  DeviceConfigurationService,
  type DeviceBrandingConfig,
  type StringOverrides,
  type TServicesParams
} from "@tivocorp/data-domain-lib";

import type { IBrandingInteractor } from "../../interfaces/IBrandingInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { Log, PostSigninApi, PostSigninLogger } from "../../logging";
import type { PostSigninApiName } from "../../logging";
import { DiagnosticSeverity } from "../../types";
import { ApplicationFeatureArea } from "../../enums";
import { DeviceFeatures, DeviceModel } from "../../device";
import { TIVO_SERVICES } from "../../../config";
import { CommonHeaders } from "../../CommonHeaders";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link MsoBrandingUiService}.
 *
 * Owns the post-sign-in branding-bundle fetch (logos, theme, MSO copy).
 * Composed by HomeScreen / PostSigninStartup / any future feature that
 * needs branding data. The {@link ApplicationFeatureArea} is fixed at
 * construction time (defaults to LOGIN for parity with the legacy call
 * site in `PostSigninInteractor`).
 *
 * Stateless: a fresh `MsoBrandingUiService` is built per call via
 * {@link ServiceLocator}.
 */
export class BrandingInteractor
  extends BaseInteractor
  implements IBrandingInteractor
{
  private static readonly TAG: string = "BrandingInteractor";

  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.LOGIN
  ) {
    super();
  }

  /**
   * Shared body for the msoName+msoServiceId device-configuration-service
   * post-signin fetches (branding bundle, string overrides): same
   * msoServiceId guard, same `DeviceConfigurationService` construction via
   * {@link ServiceLocator}, same success/failure telemetry through
   * {@link PostSigninLogger}. Kept generic over the request/response rather
   * than folded into one fetch because the two responses are consumed by
   * different downstream layers.
   */
  private async fetchDeviceConfigResource<T>(
    label: string,
    apiTelemetryKey: PostSigninApiName,
    invokeService: (
      service: DeviceConfigurationService,
      msoName: string,
      msoServiceId: string,
      signal: AbortSignal
    ) => Promise<T>,
    options?: TNetworkOptions
  ): Promise<T> {
    return this.executeGuarded(
      label,
      async (guardedSignal: AbortSignal): Promise<T> => {
        Log.d(BrandingInteractor.TAG, `${label}() called`);

        const msoName: string = DeviceModel.getTiVoSettings().getMsoName();
        if (!DeviceFeatures.isMsoServiceIdValid()) {
          Log.e(
            BrandingInteractor.TAG,
            `${label}() — no msoServiceId from device features`
          );
          throw new Error(
            `${label}() requires msoServiceId, which the device feature ` +
              "search did not provide"
          );
        }
        const msoServiceId: string = DeviceFeatures.getMsoServiceId();

        const service: DeviceConfigurationService = ServiceLocator.getInstance(
          DeviceConfigurationService,
          await this.buildDeviceConfigServicesParams()
        );
        const startTimeMs: number = Date.now();
        try {
          const response: T = await invokeService(
            service,
            msoName,
            msoServiceId,
            guardedSignal
          );
          PostSigninLogger.logApiSuccess(
            apiTelemetryKey,
            Date.now() - startTimeMs
          );
          return response;
        } catch (error: unknown) {
          PostSigninLogger.logApiFailure(
            apiTelemetryKey,
            error,
            DiagnosticSeverity.Error,
            Date.now() - startTimeMs
          );
          throw error;
        }
      },
      options
    );
  }

  public async fetchBrandingUiBundle(
    options?: TNetworkOptions
  ): Promise<DeviceBrandingConfig> {
    return this.fetchDeviceConfigResource<DeviceBrandingConfig>(
      "fetchBrandingUiBundle",
      PostSigninApi.BrandingBundle,
      (service, msoName, msoServiceId, signal) =>
        service.fetchDeviceConfig({ msoName, msoServiceId }, signal),
      options
    );
  }

  public async fetchStringOverrides(
    options?: TNetworkOptions
  ): Promise<StringOverrides> {
    return this.fetchDeviceConfigResource<StringOverrides>(
      "fetchStringOverrides",
      PostSigninApi.StringOverrides,
      (service, msoName, msoServiceId, signal) =>
        service.fetchStringOverrides({ msoName, msoServiceId }, signal),
      options
    );
  }

  // device-config-service is only in global endpoints, not post-login serviceEndpoints
  private async buildDeviceConfigServicesParams(): Promise<TServicesParams> {
    const basePath: string = await this.getServiceEndpointByServiceName(
      TIVO_SERVICES.DeviceConfigService
    );
    const common = new CommonHeaders(this.mFeatureArea);
    return common.toServicesParams({ basePath });
  }
}
